import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { departementService } from '../services/api';
import { useDepartement } from '../context/DepartementContext';
import Loader from '../components/Loader';
import './ParameterPage.css';

// ── Departement Modal (create / edit) ──────────────────────
function DepartementModal({ item, onClose, onSaved }) {
  const [form, setForm]   = useState({ name: item?.name || '', location: item?.location || '', status: item?.status ?? 1 });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const backdropRef         = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true); setError('');
    try {
      if (item) {
        await departementService.update(item.id, { ...form, status: Number(form.status) });
      } else {
        await departementService.create({ ...form, status: Number(form.status) });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  return createPortal(
    <div
      className="pm-backdrop"
      ref={backdropRef}
      onClick={(e) => e.target === backdropRef.current && onClose()}
    >
      <div className="pm-modal">
        <div className="pm-header">
          <div className="pm-header-left">
            <i className="fas fa-building pm-header-icon"></i>
            <div>
              <h3 className="pm-title">{item ? 'Edit Department' : 'New Department'}</h3>
              <p className="pm-subtitle">{item ? 'Update department details' : 'Add a new department'}</p>
            </div>
          </div>
          <button className="pm-close" onClick={onClose} type="button">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <form className="pm-body" onSubmit={handleSubmit}>
          {error && (
            <div className="pm-error">
              <i className="fas fa-exclamation-circle"></i> {error}
            </div>
          )}

          <div className="pm-field">
            <label className="pm-label">Name <span className="pm-required">*</span></label>
            <input
              className="pm-input"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. Technical Support"
              autoFocus
            />
          </div>

          <div className="pm-field">
            <label className="pm-label">Location</label>
            <input
              className="pm-input"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="e.g. Building A"
            />
          </div>

          <div className="pm-field">
            <label className="pm-label">Status</label>
            <select className="pm-select" name="status" value={form.status} onChange={handleChange}>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>

          <div className="pm-footer">
            <button type="button" className="pm-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="pm-btn-save" disabled={saving}>
              {saving
                ? <><i className="fas fa-spinner fa-spin"></i> Saving…</>
                : <><i className="fas fa-save"></i> {item ? 'Update' : 'Create'}</>}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ── Delete Confirmation ─────────────────────────────────────
function DeleteConfirm({ item, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError]       = useState('');

  const handleDelete = async () => {
    setDeleting(true); setError('');
    try {
      await departementService.delete(item.id);
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete.');
      setDeleting(false);
    }
  };

  return createPortal(
    <div className="pm-backdrop" onClick={(e) => e.target.classList.contains('pm-backdrop') && onClose()}>
      <div className="pm-modal pm-modal--sm">
        <div className="pm-header pm-header--danger">
          <div className="pm-header-left">
            <i className="fas fa-trash pm-header-icon"></i>
            <div>
              <h3 className="pm-title">Delete Department</h3>
              <p className="pm-subtitle">This action cannot be undone</p>
            </div>
          </div>
          <button className="pm-close" onClick={onClose} type="button"><i className="fas fa-times"></i></button>
        </div>
        <div className="pm-body">
          {error && <div className="pm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
          <p className="pm-confirm-text">
            Are you sure you want to delete <strong>{item.name}</strong>?
          </p>
          <div className="pm-footer">
            <button className="pm-btn-cancel" onClick={onClose} disabled={deleting}>Cancel</button>
            <button className="pm-btn-delete" onClick={handleDelete} disabled={deleting}>
              {deleting
                ? <><i className="fas fa-spinner fa-spin"></i> Deleting…</>
                : <><i className="fas fa-trash"></i> Delete</>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Page ───────────────────────────────────────────────────
function ParameterPage() {
  const { departements, loadingDepartements, refreshDepartements } = useDepartement();
  const [showModal, setShowModal]     = useState(false);
  const [editItem, setEditItem]       = useState(null);
  const [deleteItem, setDeleteItem]   = useState(null);
  const [toast, setToast]             = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleSaved = () => {
    setShowModal(false);
    setEditItem(null);
    refreshDepartements();
    showToast('Department saved successfully.');
  };

  const handleDeleted = () => {
    setDeleteItem(null);
    refreshDepartements();
    showToast('Department deleted.');
  };

  const titleSlot = document.getElementById('page-title-slot');

  return (
    <>
      {titleSlot && createPortal(
        <h1 className="param-page-title">Parameters</h1>,
        titleSlot
      )}

      {toast && (
        <div className="param-toast">
          <i className="fas fa-check-circle"></i> {toast}
        </div>
      )}

      <div className="param-section">
        {/* Section header */}
        <div className="param-section-header">
          <div className="param-section-title">
            <i className="fas fa-building"></i>
            <span>Departments</span>
            <span className="param-count">{departements.length}</span>
          </div>
          <button
            className="param-add-btn"
            onClick={() => { setEditItem(null); setShowModal(true); }}
          >
            <i className="fas fa-plus"></i> Add Department
          </button>
        </div>

        {/* Table */}
        {loadingDepartements ? (
          <Loader size="md" />
        ) : departements.length === 0 ? (
          <div className="param-empty">
            <i className="fas fa-building"></i>
            <span>No departments yet. Click "Add Department" to create one.</span>
          </div>
        ) : (
          <div className="param-table-wrap">
            <table className="param-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {departements.map((dept, idx) => (
                  <tr key={dept.id}>
                    <td className="param-td-index">{idx + 1}</td>
                    <td className="param-td-name">{dept.name}</td>
                    <td className="param-td-location">{dept.location || '—'}</td>
                    <td>
                      <span className={`param-badge ${dept.status === 1 ? 'param-badge--active' : 'param-badge--inactive'}`}>
                        {dept.status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="param-td-actions">
                      <button
                        className="param-action-btn param-action-btn--edit"
                        title="Edit"
                        onClick={() => { setEditItem(dept); setShowModal(true); }}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="param-action-btn param-action-btn--delete"
                        title="Delete"
                        onClick={() => setDeleteItem(dept)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <DepartementModal
          item={editItem}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSaved={handleSaved}
        />
      )}

      {deleteItem && (
        <DeleteConfirm
          item={deleteItem}
          onClose={() => setDeleteItem(null)}
          onDeleted={handleDeleted}
        />
      )}
    </>
  );
}

export default ParameterPage;
